import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { get, post } from '../api';
import TerrainMapPreview from '../components/TerrainMapPreview/TerrainMapPreview';
import YieldMissionModal from '../components/YieldMissionModal/YieldMissionModal';
import { DIFFICULTY_LABEL, MissionListEntry } from '../types/mission';

// The four acts, in order, with the faction each belongs to. Act membership is
// the campaign's own structure (campaign.ts) rather than anything the engine
// knows about, so the titles live here with the rest of the presentation.
const ACTS: { act: 1 | 2 | 3 | 4; title: string; faction: string }[] = [
  { act: 1, title: 'Act I', faction: 'Blue Crown' },
  { act: 2, title: 'Act II', faction: 'Red Horde' },
  { act: 3, title: 'Act III', faction: 'Yellow League' },
  { act: 4, title: 'Act IV', faction: 'Green Compact' },
];

const DIFFICULTY_COLOR: Record<string, string> = {
  'very-easy': '#5db854',
  easy: '#8bc34a',
  medium: '#f7b728',
  hard: '#e8743b',
  'very-hard': '#d43318',
};

function MissionCard({
  mission,
  onStart,
  onYield,
}: {
  mission: MissionListEntry;
  onStart: (id: string) => void;
  onYield: (gameId: number) => void;
}) {
  const hasActiveGame = mission.activeGame !== null;
  const previewTerrain =
    mission.activeGame?.preview?.terrainMap ?? mission.terrainMap;
  const previewOwners = mission.activeGame?.preview?.ownerOverrides;

  return (
    <div
      className={`box ${mission.locked ? 'has-text-grey' : ''}`}
      style={{
        borderRadius: 8,
        padding: '16px 16px 14px',
        display: 'flex',
        flexDirection: 'column',
        // A completed mission is the most common thing to scan past, so it
        // reads as done at a glance rather than needing its tag read.
        opacity: mission.locked ? 0.55 : 1,
        borderLeft: `3px solid ${
          mission.completed
            ? '#5db854'
            : DIFFICULTY_COLOR[mission.difficulty] ?? 'transparent'
        }`,
      }}
    >
      <div
        className="is-flex is-justify-content-space-between is-align-items-flex-start mb-1"
        style={{ gap: 8 }}
      >
        <h3
          style={{
            fontSize: '1rem',
            fontWeight: 600,
            wordBreak: 'break-word',
            lineHeight: 1.3,
          }}
        >
          <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>
            {mission.orderInAct}.
          </span>
          {mission.name}
        </h3>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {mission.completed && (
            <span className="tag is-success is-small">Completed</span>
          )}
          {mission.locked && (
            <span className="tag is-dark is-small">Locked</span>
          )}
          {hasActiveGame && (
            <span className="tag is-info is-small">Active</span>
          )}
        </div>
      </div>

      <p
        style={{
          fontSize: '0.8rem',
          color: 'var(--text-muted)',
          marginBottom: 6,
        }}
      >
        {mission.commander.name} — {mission.commander.title}
      </p>

      <div
        style={{
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          marginBottom: 10,
          fontSize: '0.7rem',
        }}
      >
        <span
          className="tag is-small"
          style={{
            backgroundColor: DIFFICULTY_COLOR[mission.difficulty],
            color: '#1a1a26',
            fontWeight: 600,
          }}
        >
          {DIFFICULTY_LABEL[mission.difficulty]}
        </span>
        {mission.kind === 'finale' && (
          <span className="tag is-small is-warning is-light">Finale</span>
        )}
        {!mission.fogOfWar && (
          <span className="tag is-small is-light">No Fog</span>
        )}
      </div>

      {mission.briefing && !mission.locked && (
        <p
          style={{
            fontSize: '0.78rem',
            color: 'var(--text-secondary)',
            marginBottom: 10,
            lineHeight: 1.45,
          }}
        >
          {mission.briefing.objectives[0]}
        </p>
      )}

      <div
        style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}
      >
        <TerrainMapPreview
          terrainMap={previewTerrain}
          cellSize={4}
          ownerOverrides={previewOwners}
          highlightOwners={false}
        />
      </div>

      <div
        className="is-flex is-justify-content-space-between"
        style={{ marginTop: 'auto', gap: 6 }}
      >
        <Link
          className="button is-small is-light"
          to={`/campaign/${mission.id}`}
        >
          Briefing
        </Link>
        <div style={{ display: 'flex', gap: 6 }}>
          {hasActiveGame ? (
            <>
              <button
                className="button is-small is-danger is-light"
                onClick={() => onYield(mission.activeGame!.id)}
              >
                Yield
              </button>
              <Link
                className="button is-small is-primary"
                to={`/games/${mission.activeGame!.id}?from=campaign`}
              >
                Resume
              </Link>
            </>
          ) : mission.locked ? (
            <button className="button is-small" disabled>
              Locked
            </button>
          ) : (
            <button
              className="button is-small is-primary"
              onClick={() => onStart(mission.id)}
            >
              Start
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CampaignPage() {
  const [missions, setMissions] = useState<MissionListEntry[]>([]);
  const [confirmYieldGameId, setConfirmYieldGameId] = useState<number | null>(
    null
  );
  const [yielding, setYielding] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadMissions();
  }, []);

  const loadMissions = () => {
    get('missions')
      .then(setMissions)
      .catch(() => {});
  };

  const handleStart = async (missionId: string) => {
    try {
      const result = await post(`missions/${missionId}/start`, {});
      navigate(`/games/${result.id}?from=campaign`);
    } catch {}
  };

  const handleYield = async (gameId: number) => {
    setYielding(true);
    try {
      await post(`games/${gameId}/surrender`, {});
      loadMissions();
    } catch {
    } finally {
      setYielding(false);
      setConfirmYieldGameId(null);
    }
  };

  // Group once per load rather than filtering inside the render for each act.
  const byAct = useMemo(() => {
    const groups = new Map<number, MissionListEntry[]>();
    for (const mission of missions) {
      const group = groups.get(mission.act) ?? [];
      group.push(mission);
      groups.set(mission.act, group);
    }
    groups.forEach((group) =>
      group.sort((a, b) => a.orderInAct - b.orderInAct)
    );
    return groups;
  }, [missions]);

  const total = missions.length;
  const done = missions.filter((m) => m.completed).length;

  return (
    <div>
      <div
        className="is-flex is-justify-content-space-between is-align-items-baseline"
        style={{ flexWrap: 'wrap', gap: 8 }}
      >
        <h1 className="title">Campaign</h1>
        {total > 0 && (
          <p style={{ color: 'var(--text-muted)' }}>
            {done} of {total} missions complete
          </p>
        )}
      </div>

      {ACTS.map(({ act, title, faction }) => {
        const group = byAct.get(act);
        // Acts with no missions written yet simply don't render — the campaign
        // is being built out act by act.
        if (!group || group.length === 0) {
          return null;
        }
        const actDone = group.filter((m) => m.completed).length;
        return (
          <section key={act} style={{ marginBottom: 40 }}>
            <div
              className="is-flex is-align-items-baseline"
              style={{
                gap: 12,
                marginBottom: 14,
                paddingBottom: 8,
                borderBottom: '1px solid rgba(68,68,102,0.45)',
                flexWrap: 'wrap',
              }}
            >
              <h2 className="title is-4 mb-0">{title}</h2>
              <span style={{ color: 'var(--text-secondary)' }}>{faction}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>
                {actDone}/{group.length}
              </span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 16,
              }}
            >
              {group.map((mission) => (
                <MissionCard
                  key={mission.id}
                  mission={mission}
                  onStart={handleStart}
                  onYield={setConfirmYieldGameId}
                />
              ))}
            </div>
          </section>
        );
      })}

      <YieldMissionModal
        gameId={confirmYieldGameId}
        yielding={yielding}
        onCancel={() => setConfirmYieldGameId(null)}
        onConfirm={handleYield}
      />
    </div>
  );
}
